import { useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { LanguageCode, LanguageOption } from '../../core/i18n/types';
import { languages, defaultLanguage, clientLanguageCodes } from '../../core/i18n/config';


type TFunction = {
    (key: string, options?: object): string;
    <T extends object>(key: string, options?: T): string;
  };

const availableLanguages: Partial<Record<LanguageCode, LanguageOption>> = clientLanguageCodes.reduce(
  (accumulator, code) => {
    accumulator[code] = languages[code];
    return accumulator;
  },
  {} as Partial<Record<LanguageCode, LanguageOption>>,
);

// Create the useLanguage hook
export const useLanguage = () => {
  // Directly use the useTranslation hook without type annotations
  const { i18n, t } = useTranslation();

  // Function to change the language
  const changeLanguage = useCallback(
    async (language: LanguageCode) => {
      if (i18n && typeof i18n.changeLanguage === "function") {
        await i18n.changeLanguage(language);
        localStorage.setItem('lang', language);
      } else {
        console.error("i18n.changeLanguage is not available");
      }
    },
    [i18n]
  );

  // Function to get the current language
  const getCurrentLanguage = useCallback((): LanguageCode => {
    if (i18n && i18n.language) {
      const language = i18n.language.split('-')[0];
      if (clientLanguageCodes.includes(language as LanguageCode)) {
        return language as LanguageCode;
      }
    }

    // Try from localStorage
    const storedLanguage = localStorage.getItem('lang');
    if (storedLanguage && clientLanguageCodes.includes(storedLanguage as LanguageCode)) {
      return storedLanguage as LanguageCode;
    }

    return defaultLanguage;
  }, [i18n]);

  // Current language
  const currentLanguage = getCurrentLanguage();

  return {
    t: t as TFunction,
    currentLanguage,
    availableLanguages,
    changeLanguage,
    getCurrentLanguage,
  };
};

export default useLanguage;
